import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { PaymentTypes } from '@grandvisionhq/api-client';
import { useAccount } from '@grandvisionhq/auth';
import { Context, Hooks as Hooks$1 } from '@grandvisionhq/carts-v2';
import { Util, toast } from '@grandvisionhq/common';
import { useLink, useLocation } from '@grandvisionhq/frk-routing';
import { WithUserData } from '@grandvisionhq/my-account';
import { Hooks, usePaymentsUIContext, PaymentMethod, PaymentFlow } from '@grandvisionhq/payments';
import { useIntl } from '@grandvisionhq/state';
import { pushCheckoutEventToDataLayer } from '../data-layer.js';
import { useAddAdditionalCosts } from '../hooks/carts/use-add-additional-costs.js';
import { useCreateOrder } from '../hooks/orders/use-create-order.js';
import { useCreateOrderDraft } from '../hooks/orders/use-create-order-draft.js';
import { useRedirectToOrderComplete } from '../hooks/orders/use-redirect-to-order-confirm-page.js';
import { ApplePaySessionErrors } from '../mappers/errors-mapping.js';
import { StepPayment } from './StepPayment.js';
import { useGetQueryParams } from '../utils/get-query-params.js';
import { useGetLabels } from './hooks/use-get-labels.js';
import { useApplePay } from '../modules/ExpressCheckout/hooks/apple-pay/use-apple-pay.js';
import { useHandleQueryParams } from './hooks/use-handle-query-params.js';
import { useHandleQrFormUpdates } from './hooks/use-handle-qr-form-updates.js';
import { useCheckoutSettings } from '../context/CheckoutSettings/context.js';

const StepPaymentContainer = ({ onPrevStep , selfUrl: selfUrl1 , updateCheckoutStatus  })=>{
    const { status , cartId , type: paymentType  } = useGetQueryParams();
    const [labels] = useGetLabels();
    const redirectToOrderComplete = useRedirectToOrderComplete();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState();
    const [payCartWithApplePay] = useApplePay();
    const isApplePayActive = Hooks.useIsApplePayActive();
    // we need the recover session logic from this hook after a failed payment
    const authState = useAccount();
    const { addAdditionalPaymentCostsIfApplicable  } = useAddAdditionalCosts();
    const createOrderDraft = useCreateOrderDraft();
    const [createOrder, { orderResponse , error: createOrderError , paymentFlow , setPaymentFlow  }] = useCreateOrder();
    const [isLoading, setIsLoading] = React.useState(false);
    const [updateUrlQueryParam, deleteQueryParam] = useHandleQueryParams();
    const { href: homeHref  } = useLink({
        route: 'home'
    }, {
        replace: true
    }) || {};
    const { replace  } = useLocation();
    const { cart  } = Context.useCart();
    const [_, { reset  }] = Hooks$1.useCartStorage();
    const { currency  } = useIntl();
    const { isQrCodeModalOpen  } = usePaymentsUIContext();
    const prevIsQrCodeModalOpenValue = Util.usePrevious(isQrCodeModalOpen);
    React.useEffect(()=>{
        if (paymentType === PaymentMethod.APPLE_PAY && !isApplePayActive) {
            // this could happen if somebody is accessing the page directly in an unsupported browser
            toast.failed(ApplePaySessionErrors.ApplePaySessionUnsupported);
            deleteQueryParam([
                'type'
            ]);
        }
    }, [
        deleteQueryParam,
        isApplePayActive,
        paymentType,
        updateUrlQueryParam
    ]);
    React.useEffect(()=>{
        if ((cart === null || cart === void 0 ? void 0 : cart.checkoutState) == 'Finalized') {
            reset();
            homeHref && replace(homeHref);
        }
    }, [
        cart === null || cart === void 0 ? void 0 : cart.checkoutState,
        homeHref,
        replace,
        reset,
        cartId,
        cart === null || cart === void 0 ? void 0 : cart.id
    ]);
    React.useEffect(()=>{
        if (createOrderError) {
            updateCheckoutStatus && updateCheckoutStatus('failed');
        }
    }, [
        createOrderError,
        updateCheckoutStatus
    ]);
    React.useEffect(()=>{
        /**
     *  Fire data layer events
     */ cartId && cart && pushCheckoutEventToDataLayer(cart, 'payment', currency);
    // Data layer event need to be fired only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    React.useEffect(()=>{
        /**
     *  Reset Payment flow when cartId changes, when QrCodeModal is being closed
     */ if (prevIsQrCodeModalOpenValue !== isQrCodeModalOpen) {
            if (cartId && !isQrCodeModalOpen) {
                setPaymentFlow(PaymentFlow.UNDEFINED);
            }
        }
    }, [
        cartId,
        isQrCodeModalOpen,
        prevIsQrCodeModalOpenValue,
        setPaymentFlow
    ]);
    React.useEffect(()=>{
        // Handle redirect for failed payment status
        if (status && (status === PaymentTypes.PaymentResult.Cancelled || status === PaymentTypes.PaymentResult.Refused || status === PaymentTypes.PaymentResult.Error || status === PaymentTypes.PaymentResult.InternalError)) {
            updateCheckoutStatus && updateCheckoutStatus('failed');
        }
    // This code works as default constructor and need to be run only once when page/component loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        status,
        authState
    ]);
    const { paymentMethodLogos  } = useCheckoutSettings();
    /**
   * @description
   *  Callback method to provide details of selected payment method
   * @param paymentMethodConfig
   *  config of selected Payment method
   */ const handleOnPaymentMethodSelected = React.useCallback((paymentMethodConfig)=>{
        setSelectedPaymentMethod(paymentMethodConfig);
        updateUrlQueryParam({
            type: paymentMethodConfig.type
        });
        /**
       *  Condition to change error status when user try to
       *  complete payment using different method
       */ if ((selectedPaymentMethod === null || selectedPaymentMethod === void 0 ? void 0 : selectedPaymentMethod.type) && (selectedPaymentMethod === null || selectedPaymentMethod === void 0 ? void 0 : selectedPaymentMethod.type) !== paymentMethodConfig.type && status !== '') {
            updateCheckoutStatus && updateCheckoutStatus('');
            deleteQueryParam([
                'status'
            ]);
        }
    }, [
        deleteQueryParam,
        selectedPaymentMethod === null || selectedPaymentMethod === void 0 ? void 0 : selectedPaymentMethod.type,
        status,
        updateCheckoutStatus,
        updateUrlQueryParam, 
    ]);
    /**
   * @description
   *  Handler to convert existing cart into order
   */ const handleCreateOrder = React.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(selfUrl, paymentDetails) {
            if (typeof cartId === 'string' && selectedPaymentMethod) {
                setIsLoading(true);
                const orderPayload = yield createOrderDraft({
                    paymentDetails,
                    selectedPaymentMethod,
                    returnUrl: `${selfUrl}${`/api/payment-callback?type=${selectedPaymentMethod.type}`}`
                });
                const createOrderResult = yield createOrder(orderPayload);
                if (createOrderResult && createOrderResult.success && !createOrderResult.redirect && createOrderResult.resultType !== 'QRCODE') {
                    try {
                        redirectToOrderComplete({
                            cartId: createOrderResult === null || createOrderResult === void 0 ? void 0 : createOrderResult.cartId,
                            orderData: createOrderResult === null || createOrderResult === void 0 ? void 0 : createOrderResult.orderDataToken
                        });
                    } catch (error) {
                        console.error(error);
                        setIsLoading(false);
                    }
                } else {
                    setIsLoading(false);
                }
            }
        });
        return function(selfUrl, paymentDetails) {
            return _ref.apply(this, arguments);
        };
    }(), [
        cartId,
        createOrder,
        createOrderDraft,
        redirectToOrderComplete,
        selectedPaymentMethod
    ]);
    const isPaymentStepValid = React.useCallback((paymentDetails)=>{
        const isValid = paymentDetails && paymentDetails.isValid;
        if (!isValid) {
            toast.failed('checkout.errors.form.payment.details');
        }
        return Boolean(isValid);
    }, []);
    const handleNextClick = React.useCallback(({ paymentDetailsConfig  } = {})=>{
        if (isPaymentStepValid(paymentDetailsConfig)) {
            if ((selectedPaymentMethod === null || selectedPaymentMethod === void 0 ? void 0 : selectedPaymentMethod.type) === PaymentMethod.APPLE_PAY) {
                updateCheckoutStatus && updateCheckoutStatus('');
                payCartWithApplePay === null || payCartWithApplePay === void 0 ? void 0 : payCartWithApplePay();
            } else {
                updateCheckoutStatus && updateCheckoutStatus('');
                paymentDetailsConfig ? handleCreateOrder(selfUrl1, paymentDetailsConfig) : handleCreateOrder(selfUrl1);
            }
        }
    }, [
        isPaymentStepValid,
        selectedPaymentMethod === null || selectedPaymentMethod === void 0 ? void 0 : selectedPaymentMethod.type,
        updateCheckoutStatus,
        payCartWithApplePay,
        handleCreateOrder,
        selfUrl1, 
    ]);
    const handlePreviousClick = React.useCallback(()=>{
        onPrevStep && onPrevStep();
    }, [
        onPrevStep
    ]);
    var ref;
    const paymentMethodType = (ref = selectedPaymentMethod === null || selectedPaymentMethod === void 0 ? void 0 : selectedPaymentMethod.type) !== null && ref !== void 0 ? ref : 'swish';
    /**
   * @description
   * qrCodeFormData is needed for the new paymentMethodsUI and
   * useHandleQrFormUpdates hook also handles the QrForm state changes
   */ const [qrCodeFormData] = useHandleQrFormUpdates(swcHelpers.objectSpread({}, orderResponse ? {
        qrPaymentConfig: {
            qrCodeExpiresIn: orderResponse.qrCodeExpiresIn,
            qrCodeUrl: orderResponse.qrCodeUrl,
            redirectUrl: orderResponse.redirectUrl
        }
    } : {}, {
        paymentFlow,
        selfUrl: selfUrl1,
        paymentMethodType,
        paymentMethodLogos,
        orderId: paymentFlow === PaymentFlow.QR_CODE ? orderResponse === null || orderResponse === void 0 ? void 0 : orderResponse.orderId : undefined,
        orderDataToken: orderResponse === null || orderResponse === void 0 ? void 0 : orderResponse.orderDataToken
    }));
    /* onHandlePayment */ const onHandlePayment = React.useCallback((selectedPaymentConfig)=>{
        selectedPaymentConfig && updateUrlQueryParam({
            type: selectedPaymentConfig.type
        });
        handleNextClick({
            paymentDetailsConfig: selectedPaymentConfig
        });
    }, [
        updateUrlQueryParam,
        handleNextClick
    ]);
    var ref1;
    return /*#__PURE__*/ React.createElement(StepPayment, {
        onNextClick: handleNextClick,
        onPrevClick: handlePreviousClick,
        onHandlePayment: onHandlePayment,
        qrCodeForm: qrCodeFormData,
        selectedPaymentMethod: (ref1 = paymentType) !== null && ref1 !== void 0 ? ref1 : '',
        onPaymentMethodSelected: handleOnPaymentMethodSelected,
        cartId: cartId,
        addAdditionalPaymentCosts: addAdditionalPaymentCostsIfApplicable,
        labels: labels,
        orderInProgress: isLoading
    });
};
const StepPaymentContainerWithUserData = WithUserData(StepPaymentContainer);
var StepPaymentContainerWithUserData$1 = StepPaymentContainerWithUserData;

export { StepPaymentContainerWithUserData$1 as default };
//# sourceMappingURL=StepPaymentContainer.js.map
